
import React, { useState, useEffect, lazy, Suspense } from "react";
import SystemHealth from "@/pages/admin/SystemHealth";
import QuizGeneration from "@/pages/admin/QuizGeneration";
import { ModuleDataQuality } from "@/pages/admin/ModuleDataQuality";
import ResourceGenerator from "@/pages/admin/ResourceGenerator";
import BuildDashboard from "@/pages/admin/BuildDashboard";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { useSupabaseAuth } from "@/hooks/auth";
import { validateSessionHealth, clearInvalidSession } from "@/utils/sessionValidator";
import { useIsFetching } from "@tanstack/react-query";
import { GlobalLoadingBar } from "@/components/ui/enhanced-loading";
import AppShellSkeleton from "@/components/dashboard/AppShellSkeleton";
import AuthErrorBoundary from "@/components/error/AuthErrorBoundary";
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { NavigationAwareErrorBoundary } from '@/components/error/NavigationAwareErrorBoundary';
import GlobalErrorBoundary from "@/components/error/GlobalErrorBoundary";
import CourseErrorBoundary from "@/components/error/CourseErrorBoundary";
import MainLayout from "@/components/MainLayout";
import NavigationAnalyticsProvider from "@/components/navigation/NavigationAnalytics";
import { SecurityInitializer } from "@/components/security/SecurityInitializer";
import { ContentGenerationBootstrap } from "@/components/admin/ContentGenerationBootstrap";
import { MasterContentBootstrap } from "@/components/admin/MasterContentBootstrap";
import { FocusMusicProvider } from "@/hooks/useFocusMusic";
import FocusMusicWidget from "@/components/audio/FocusMusicWidget";
import CSPConfig from "@/components/security/CSPConfig";
import { FeatureFlagProvider } from "@/providers/FeatureFlagProvider";
import NoBreakageSmokeTests from "@/components/dev/NoBreakageSmokeTests";
import { PWAInstallPrompt } from "@/components/pwa/PWAInstallPrompt";
import { OfflineIndicator } from "@/components/pwa/OfflineIndicator";
import { UpdatePrompt } from "@/components/pwa/UpdatePrompt";
import { usePWA } from "@/hooks/usePWA";
import { AudioProvider } from "@/contexts/AudioContext";
import { MiniAudioPlayer } from "@/components/audio/MiniAudioPlayer";
import { NavigationAudioListener } from "@/components/audio/NavigationAudioListener";
import { useAudioNavigationCleanup } from "@/hooks/useAudioNavigationCleanup";
// Migration components removed - content enhancement complete (97%)
import { Loader2 } from "lucide-react";
import { pwaService } from "@/services/PWAService";
import { Navigate } from "react-router-dom";
import RoleProtectedRoute from "@/components/RoleProtectedRoute";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import BottomNavigation from "@/components/mobile/BottomNavigation";
import { SkipNavigation } from "@/components/accessibility/SkipNavigation";
import { PageTransition } from "@/components/transitions/PageTransition";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRouteLoadingWatchdog } from "@/hooks/useRouteLoadingWatchdog";
import { CanonicalModuleRedirect } from "@/components/routing/CanonicalModuleRedirect";

const ProgressPage = lazy(() => import("@/pages/ProgressPage"));

// Public pages (no layout)
import Index from "@/pages/Index";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SignUp from "@/pages/SignUp";
import CertificateVerify from "@/pages/CertificateVerify";
import MyCertificates from "@/pages/MyCertificates";
import CertificateManagement from "@/pages/admin/CertificateManagement";
import InvalidCertificates from "@/pages/admin/InvalidCertificates";
import ChangePassword from "@/pages/ChangePassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import PasswordChangeGuard from "@/components/auth/PasswordChangeGuard";

// Protected pages (with persistent layout)
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Certificates from "@/pages/Certificates";
import Notifications from "@/pages/Notifications";

import AITutor from "@/pages/AITutor";
import StudentHelp from "@/pages/StudentHelp";
import Admin from "@/pages/Admin";
import AdminModuleDurationFix from "@/pages/AdminModuleDurationFix";
import Staff from "@/pages/Staff";
import StaffUserManagement from "@/pages/StaffUserManagement";
import CourseSearchPage from "@/pages/CourseSearchPage";
import FavoritesPage from "@/pages/FavoritesPage";
import ErrorRecoveryValidation from "@/pages/ErrorRecoveryValidation";

// PHASE 3: Route-based code splitting - Lazy load ALL course pages for smaller initial bundle
const Courses = lazy(() => import("@/pages/Courses"));
const CourseDetail = lazy(() => import("@/pages/CourseDetail"));
const ModulePage = lazy(() => import("@/pages/ModulePage"));
const UniversalModulePage = lazy(() => import("@/pages/UniversalModulePage"));
const UniversalCoursePage = lazy(() => import("@/pages/UniversalCoursePage"));
const ReactCourses = lazy(() => import("@/pages/ReactCourses"));

// SQL Course pages
const SQLCourses = lazy(() => import("@/pages/SQLCourses"));
const SQLCourseDetail = lazy(() => import("@/pages/SQLCourseDetail"));
const SQLModulePage = lazy(() => import("@/pages/SQLModulePage"));
const SQLPlayground = lazy(() => import("@/pages/SQLPlayground"));
const WebContainerDemo = lazy(() => import("@/pages/WebContainerDemo"));

// Java Course pages
const JavaCourses = lazy(() => import("@/pages/JavaCourses"));
const JavaCourseDetail = lazy(() => import("@/pages/JavaCourseDetail"));
const JavaModulePage = lazy(() => import("@/pages/JavaModulePage"));

// AI Course pages
const AICourses = lazy(() => import("@/pages/AICourses"));
const AICourseDetail = lazy(() => import("@/pages/AICourseDetail"));
const AIModulePage = lazy(() => import("@/pages/AIModulePage"));

// Other course pages
const PythonCourses = lazy(() => import("@/pages/PythonCourses"));
const PracticeLabFullPage = lazy(() => import("@/pages/PracticeLabFullPage"));
const PythonCourseDetail = lazy(() => import("@/pages/PythonCourseDetail"));
const PythonModulePage = lazy(() => import("@/pages/PythonModulePage"));
const JavaScriptCourses = lazy(() => import("@/pages/JavaScriptCourses"));
const JavaScriptCourseDetail = lazy(() => import("@/pages/JavaScriptCourseDetail"));
const JavaScriptModulePage = lazy(() => import("@/pages/JavaScriptModulePage"));
const AWSCourses = lazy(() => import("@/pages/AWSCourses"));
const AWSCourseDetail = lazy(() => import("@/pages/AWSCourseDetail"));
const AWSModulePage = lazy(() => import("@/pages/AWSModulePage"));
const DotNetCourses = lazy(() => import("@/pages/DotNetCourses"));
const DotNetCourseDetail = lazy(() => import("@/pages/DotNetCourseDetail"));
const DotNetModulePage = lazy(() => import("@/pages/DotNetModulePage"));
const GCPCourses = lazy(() => import("@/pages/GCPCourses"));
const GCPCourseDetail = lazy(() => import("@/pages/GCPCourseDetail"));
const GCPModulePage = lazy(() => import("@/pages/GCPModulePage"));
const DataScienceCourses = lazy(() => import("@/pages/DataScienceCourses"));
const DataScienceCourseDetail = lazy(() => import("@/pages/DataScienceCourseDetail"));
const DataScienceModulePage = lazy(() => import("@/pages/DataScienceModulePage"));
const DataEngineeringCourses = lazy(() => import("@/pages/DataEngineeringCourses"));
const DataEngineeringCourseDetail = lazy(() => import("@/pages/DataEngineeringCourseDetail"));
const DataEngineeringModulePage = lazy(() => import("@/pages/DataEngineeringModulePage"));
const MLCourses = lazy(() => import("@/pages/MLCourses"));
const MLCourseDetail = lazy(() => import("@/pages/MLCourseDetail"));
const MLModulePage = lazy(() => import("@/pages/MLModulePage"));
const PowerBICourses = lazy(() => import("@/pages/PowerBICourses"));
const PowerBICourseDetail = lazy(() => import("@/pages/PowerBICourseDetail"));
const PowerBIModulePage = lazy(() => import("@/pages/PowerBIModulePage"));
const TableauCourses = lazy(() => import("@/pages/TableauCourses"));
const TableauCourseDetail = lazy(() => import("@/pages/TableauCourseDetail"));
const TableauModulePage = lazy(() => import("@/pages/TableauModulePage"));
const AzureCoursesPage = lazy(() => import("@/pages/azure/AzureCoursesPage"));
const AzureCourseDetail = lazy(() => import("@/pages/azure/AzureCourseDetail"));
const AzureModulePage = lazy(() => import("@/pages/azure/AzureModulePage"));
const WorkflowAutomationCourses = lazy(() => import("@/pages/WorkflowAutomationCourses"));
const WorkflowAutomationCourseDetail = lazy(() => import("@/pages/WorkflowAutomationCourseDetail"));
const WorkflowAutomationModulePage = lazy(() => import("@/pages/WorkflowAutomationModulePage"));

// Admin/Staff pages
import UserManagement from "@/pages/admin/UserManagement";
import SystemSettings from "@/pages/SystemSettings";
import SecurityAuthSettings from "@/pages/admin/SecurityAuthSettings";
import DatabaseSettings from "@/pages/admin/DatabaseSettings";
import NotificationSettings from "@/pages/admin/NotificationSettings";
import UserManagementSettings from "@/pages/admin/UserManagementSettings";
import SystemConfigurationSettings from "@/pages/admin/SystemConfigurationSettings";
import PrivacyComplianceSettings from "@/pages/admin/PrivacyComplianceSettings";
import SystemHealthDashboard from '@/pages/admin/SystemHealthDashboard';
import PreLaunchChecklist from '@/pages/admin/PreLaunchChecklist';
import ContentEnhancementFix from '@/pages/admin/ContentEnhancementFix';
import ContentQualityMonitor from '@/pages/admin/ContentQualityMonitor';
import ContentGapMonitor from '@/pages/admin/ContentGapMonitor';
import ModuleGeneration from '@/pages/admin/ModuleGeneration';
import AudioGeneration from '@/pages/admin/AudioGeneration';
import SSGBuilder from '@/pages/admin/SSGBuilder';

import NotFound from "@/pages/NotFound";
import LearningPaths from "@/pages/LearningPaths";
import { LearningPathDetail } from '@/components/learningPaths/LearningPathDetail';
import { SkillAssessmentPage } from '@/pages/SkillAssessmentPage';
import { AdvancedFeatures } from '@/pages/AdvancedFeatures';
import AdminTools from '@/pages/AdminTools';
import CourseSyncUtility from '@/pages/admin/CourseSyncUtility';
import CourseHealthDashboard from '@/components/admin/CourseHealthDashboard';
import QuizAuditReport from '@/pages/admin/QuizAuditReport';
import ContentQualityDashboard from '@/pages/admin/ContentQualityDashboard';
import AudioGenerationPage from '@/pages/admin/AudioGeneration';
import AudioPlayerExample from '@/components/examples/AudioPlayerExample';
import InstallApp from '@/pages/InstallApp';

// Protected Layout Component - wraps all authenticated routes
const ProtectedLayout = ({ children }: { children: React.ReactNode }) => {
  return <MainLayout>{children}</MainLayout>;
};

// Auth-aware routing component
const AppRoutes = () => {
  const { session, user, isLoading } = useSupabaseAuth();
  
  // Enable route loading watchdog
  useRouteLoadingWatchdog();
  
  // Enable global audio navigation cleanup
  useAudioNavigationCleanup();
  
  // Validate session health on mount
  useEffect(() => {
    const validateSession = async () => {
      const health = await validateSessionHealth();
      if (!health.valid) {
        console.log(`🔒 Invalid session detected: ${health.reason}`);
        await clearInvalidSession();
      }
    };
    validateSession();
  }, []);

  // Show app shell skeleton instead of blocking loader
  if (isLoading) {
    return <AppShellSkeleton />;
  }

  // If user is authenticated, wrap protected routes with persistent layout
  if (session && user) {
    return (
      <>
        <SkipNavigation />
        <Routes>
          {/* Special route for password change - accessible to authenticated users */}
          <Route path="/change-password" element={<ChangePassword />} />
          
          {/* All other routes protected by PasswordChangeGuard */}
          <Route path="/*" element={
            <PasswordChangeGuard>
              <ProtectedLayout>
                <NavigationAnalyticsProvider>
                  <Routes>
          {/* Dashboard and core pages */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/Dashboard" element={<Dashboard />} /> {/* Route alias for capitalization */}
          <Route path="/progress" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <ProgressPage />
            </Suspense>
          } />
          <Route path="/profile" element={<Profile />} />
          <Route path="/Profile" element={<Profile />} /> {/* Route alias for capitalization */}
          <Route path="/settings" element={<Settings />} />
          <Route path="/certificates" element={<Certificates />} />
          <Route path="/my-certificates" element={<MyCertificates />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/ai-tutor" element={<AITutor />} />
          <Route path="/student-help" element={<StudentHelp />} />
          
          {/* PWA Installation Page */}
          <Route path="/install" element={<InstallApp />} />
          
          {/* New search and favorites features */}
          <Route path="/search-courses" element={<CourseSearchPage />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          
          {/* All Courses page - PHASE 3: Wrapped with Suspense */}
          <Route path="/courses" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <Courses />
            </Suspense>
          } />
          
          {/* Audio Player Demo */}
          <Route path="/audio-demo" element={<AudioPlayerExample />} />
          
          {/* Learning Paths */}
        <Route path="/learning-paths" element={<LearningPaths />} />
        <Route path="/learning-paths/:trackId" element={<LearningPathDetail />} />
        <Route path="/assessment" element={<SkillAssessmentPage />} />
        
        {/* Advanced Features */}
        <Route path="/advanced-features" element={<AdvancedFeatures />} />
          
          
          {/* React Development courses */}
          <Route path="/react-courses" element={<ReactCourses />} />
          
           {/* Admin and Staff routes */}
           <Route path="/admin" element={<Admin />} />
          <Route path="/admin/module-duration-fix" element={<AdminModuleDurationFix />} />
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/settings" element={<SystemSettings />} />
          <Route path="/admin/settings/security" element={<SecurityAuthSettings />} />
          <Route path="/admin/settings/database" element={<DatabaseSettings />} />
          <Route path="/admin/settings/notifications" element={<NotificationSettings />} />
          <Route path="/admin/settings/users" element={<UserManagementSettings />} />
          <Route path="/admin/settings/system" element={<SystemConfigurationSettings />} />
           <Route path="/admin/settings/privacy" element={<PrivacyComplianceSettings />} />
           <Route path="/admin/tools" element={<AdminTools />} />
           <Route path="/admin/course-sync" element={<CourseSyncUtility />} />
           <Route path="/admin/data-health" element={<CourseHealthDashboard />} />
            <Route path="/admin/quiz-audit" element={<QuizAuditReport />} />
            <Route path="/admin/content-quality" element={<ContentQualityDashboard />} />
            <Route path="/admin/system-health" element={<SystemHealthDashboard />} />
            <Route path="/admin/build-dashboard" element={<BuildDashboard />} />
            <Route path="/admin/audio-generation" element={<AudioGenerationPage />} />
           <Route path="/admin/pre-launch" element={<PreLaunchChecklist />} />
            <Route path="/admin/content-fix" element={<ContentEnhancementFix />} />
            <Route path="/admin/content-monitor" element={<ContentQualityMonitor />} />
            <Route path="/admin/content-gaps" element={<ContentGapMonitor />} />
            <Route path="/admin/module-generation" element={<ModuleGeneration />} />
            <Route path="/admin/audio-generation" element={<AudioGeneration />} />
            <Route path="/admin/resource-generator" element={<ResourceGenerator />} />
            <Route path="/admin/system-health" element={<SystemHealth />} />
           <Route path="/admin/quiz-generation" element={<QuizGeneration />} />
           <Route path="/admin/module-data-quality" element={<ModuleDataQuality />} />
            <Route path="/admin/certificates" element={<CertificateManagement />} />
            <Route path="/admin/invalid-certificates" element={<InvalidCertificates />} />
            
            {/* PHASE 6: Error Recovery Validation */}
            <Route path="/admin/error-recovery-validation" element={<ErrorRecoveryValidation />} />
            
            <Route path="/admin/ssg-builder" element={
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
                <SSGBuilder />
              </Suspense>
            } />
           <Route path="/staff/*" element={
             <RoleProtectedRoute allowedRoles={['staff', 'admin']}>
               <Staff />
             </RoleProtectedRoute>
           } />
           <Route path="/staff/users" element={
             <RoleProtectedRoute allowedRoles={['staff', 'admin']}>
               <StaffUserManagement />
             </RoleProtectedRoute>
            } />
          
          {/* Practice Lab - Full-Page Route with lazy loading */}
          <Route path="/practice/:courseId/:moduleId" element={
            <Suspense fallback={
              <div className="h-screen flex items-center justify-center bg-background">
                <div className="text-center">
                  <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading Practice Lab...</p>
                </div>
              </div>
            }>
              <PracticeLabFullPage />
            </Suspense>
          } />
          
          {/* Basic React courses - PHASE 3: All routes wrapped with Suspense */}
          <Route path="/courses/:courseId" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <CourseDetail />
            </Suspense>
          } />
          <Route path="/courses/:courseId/:moduleId" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <CanonicalModuleRedirect />
              <ModulePage />
            </Suspense>
          } />
          <Route path="/modules/:moduleId" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <UniversalModulePage />
            </Suspense>
          } />
          
          {/* SQL courses */}
          <Route path="/sql-courses" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <SQLCourses />
            </Suspense>
          } />
          <Route path="/sql-courses/:courseId" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <SQLCourseDetail />
            </Suspense>
          } />
          <Route path="/sql-courses/:courseId/:moduleId" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <SQLModulePage />
            </Suspense>
          } />
          <Route path="/sql-playground" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <SQLPlayground />
            </Suspense>
          } />
          
          {/* AI courses */}
          <Route path="/ai-courses" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <AICourses />
            </Suspense>
          } />
          <Route path="/ai-courses/:courseId" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <AICourseDetail />
            </Suspense>
          } />
          <Route path="/ai-courses/:courseId/:moduleId" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <AIModulePage />
            </Suspense>
          } />
          
          {/* Python courses */}
          <Route path="/python-courses" element={<PythonCourses />} />
          <Route path="/python-courses/:courseId" element={<PythonCourseDetail />} />
          <Route path="/python-courses/:courseId/:moduleId" element={<PythonModulePage />} />
          
          {/* JavaScript courses */}
          <Route path="/javascript-courses" element={<JavaScriptCourses />} />
          <Route path="/javascript-courses/:courseId" element={<JavaScriptCourseDetail />} />
          <Route path="/javascript-courses/:courseId/:moduleId" element={<JavaScriptModulePage />} />
          
          {/* Java courses */}
          <Route path="/java-courses" element={<JavaCourses />} />
          <Route path="/java-courses/:courseId" element={<JavaCourseDetail />} />
          <Route path="/java-courses/:courseId/:moduleId" element={<JavaModulePage />} />
          <Route path="/webcontainer-demo" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <WebContainerDemo />
            </Suspense>
          } />
          
          {/* AWS courses */}
          <Route path="/aws-courses" element={<AWSCourses />} />
          <Route path="/aws-courses/:courseId" element={<AWSCourseDetail />} />
          <Route path="/aws-courses/:courseId/:moduleId" element={<AWSModulePage />} />
          
          {/* .NET courses */}
          <Route path="/dotnet-courses" element={<DotNetCourses />} />
          <Route path="/dotnet-courses/:courseId" element={<DotNetCourseDetail />} />
          <Route path="/dotnet-courses/:courseId/:moduleId" element={<DotNetModulePage />} />
          
          {/* GCP courses */}
          <Route path="/gcp-courses" element={<GCPCourses />} />
          <Route path="/gcp-courses/:courseId" element={<GCPCourseDetail />} />
          <Route path="/gcp-courses/:courseId/:moduleId" element={<GCPModulePage />} />
          
          {/* Data Science courses */}
          <Route path="/datascience-courses" element={<DataScienceCourses />} />
          <Route path="/datascience-courses/:courseId" element={<DataScienceCourseDetail />} />
          <Route path="/datascience-courses/:courseId/:moduleId" element={<DataScienceModulePage />} />
          
          {/* Data Engineering courses */}
          <Route path="/dataengineering-courses" element={<DataEngineeringCourses />} />
          <Route path="/dataengineering-courses/:courseId" element={<DataEngineeringCourseDetail />} />
          <Route path="/dataengineering-courses/:courseId/:moduleId" element={<DataEngineeringModulePage />} />
          
          {/* ETL Developer courses */}
          <Route path="/etl-courses" element={<UniversalCoursePage courseType="etl" />} />
          <Route path="/etl-courses/:courseId" element={<UniversalCoursePage courseType="etl" />} />
          <Route path="/etl-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="etl" />} />
          
          {/* ML courses */}
          <Route path="/ml-courses" element={<MLCourses />} />
          <Route path="/ml-courses/:courseId" element={<MLCourseDetail />} />
          <Route path="/ml-courses/:courseId/:moduleId" element={<MLModulePage />} />
          
          {/* PowerBI courses */}
          <Route path="/powerbi-courses" element={<PowerBICourses />} />
          <Route path="/powerbi-courses/:courseId" element={<PowerBICourseDetail />} />
          <Route path="/powerbi-courses/:courseId/:moduleId" element={<PowerBIModulePage />} />
          
          {/* Tableau courses */}
          <Route path="/tableau-courses" element={<TableauCourses />} />
          <Route path="/tableau-courses/:courseId" element={<TableauCourseDetail />} />
          <Route path="/tableau-courses/:courseId/:moduleId" element={<TableauModulePage />} />
          
          {/* Azure courses */}
          <Route path="/azure-courses" element={<AzureCoursesPage />} />
          <Route path="/azure-courses/:courseId" element={<AzureCourseDetail />} />
          <Route path="/azure-courses/:courseId/:moduleId" element={<AzureModulePage />} />
          
          {/* Workflow Automation courses */}
          <Route path="/workflow-automation-courses" element={<WorkflowAutomationCourses />} />
          <Route path="/workflow-automation-courses/:courseId" element={<WorkflowAutomationCourseDetail />} />
          <Route path="/workflow-automation-courses/:courseId/:moduleId" element={<WorkflowAutomationModulePage />} />
          
          {/* Mobile Development courses */}
          <Route path="/mobile-courses" element={<UniversalCoursePage courseType="mobile" />} />
          <Route path="/mobile-courses/:courseId" element={<UniversalCoursePage courseType="mobile" />} />
          <Route path="/mobile-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="mobile" />} />
          
          {/* Backend Development courses */}
          <Route path="/backend-courses" element={<UniversalCoursePage courseType="backend" />} />
          <Route path="/backend-courses/:courseId" element={<UniversalCoursePage courseType="backend" />} />
          <Route path="/backend-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="backend" />} />
          
          {/* Database Technology courses */}
          <Route path="/database-courses" element={<UniversalCoursePage courseType="database" />} />
          <Route path="/database-courses/:courseId" element={<UniversalCoursePage courseType="database" />} />
          <Route path="/database-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="database" />} />
          
          {/* DevOps courses */}
          <Route path="/devops-courses" element={<UniversalCoursePage courseType="devops" />} />
          <Route path="/devops-courses/:courseId" element={<UniversalCoursePage courseType="devops" />} />
          <Route path="/devops-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="devops" />} />
          
          {/* Application Security courses */}
          <Route path="/appsec-courses" element={<UniversalCoursePage courseType="appsec" />} />
          <Route path="/appsec-courses/:courseId" element={<UniversalCoursePage courseType="appsec" />} />
          <Route path="/appsec-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="appsec" />} />
          
          {/* Security courses */}
          <Route path="/security-courses" element={<UniversalCoursePage courseType="security" />} />
          <Route path="/security-courses/:courseId" element={<UniversalCoursePage courseType="security" />} />
          <Route path="/security-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="security" />} />
          
          {/* Blockchain courses */}
          <Route path="/blockchain-courses" element={<UniversalCoursePage courseType="blockchain" />} />
          <Route path="/blockchain-courses/:courseId" element={<UniversalCoursePage courseType="blockchain" />} />
          <Route path="/blockchain-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="blockchain" />} />
          
          {/* Game Development courses */}
          <Route path="/game-development-courses" element={<UniversalCoursePage courseType="game-development" />} />
          <Route path="/game-development-courses/:courseId" element={<UniversalCoursePage courseType="game-development" />} />
          <Route path="/game-development-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="game-development" />} />
          
          {/* UI/UX Design courses */}
          <Route path="/ui-ux-courses" element={<UniversalCoursePage courseType="ui-ux-design" />} />
          <Route path="/ui-ux-courses/:courseId" element={<UniversalCoursePage courseType="ui-ux-design" />} />
          <Route path="/ui-ux-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="ui-ux-design" />} />
          
          {/* Graphics Programming courses */}
          <Route path="/graphics-courses" element={<UniversalCoursePage courseType="graphics-programming" />} />
          <Route path="/graphics-courses/:courseId" element={<UniversalCoursePage courseType="graphics-programming" />} />
          <Route path="/graphics-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="graphics-programming" />} />
          
          {/* Enterprise Training courses */}
          <Route path="/enterprise-courses" element={<UniversalCoursePage courseType="enterprise-training" />} />
          <Route path="/enterprise-courses/:courseId" element={<UniversalCoursePage courseType="enterprise-training" />} />
          <Route path="/enterprise-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="enterprise-training" />} />
          
          {/* Professional Development courses */}
          <Route path="/professional-development-courses" element={<UniversalCoursePage courseType="professional-development" />} />
          <Route path="/professional-development-courses/:courseId" element={<UniversalCoursePage courseType="professional-development" />} />
          <Route path="/professional-development-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="professional-development" />} />
          
          {/* Infrastructure courses */}
          <Route path="/infrastructure-courses" element={<UniversalCoursePage courseType="infrastructure" />} />
          <Route path="/infrastructure-courses/:courseId" element={<UniversalCoursePage courseType="infrastructure" />} />
          <Route path="/infrastructure-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="infrastructure" />} />
          
          {/* Frontend Advanced courses */}
          <Route path="/frontend-advanced-courses" element={<UniversalCoursePage courseType="frontend-advanced" />} />
          <Route path="/frontend-advanced-courses/:courseId" element={<UniversalCoursePage courseType="frontend-advanced" />} />
          <Route path="/frontend-advanced-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="frontend-advanced" />} />
          
          {/* Testing Modern courses */}
          <Route path="/testing-modern-courses" element={<UniversalCoursePage courseType="testing-modern" />} />
          <Route path="/testing-modern-courses/:courseId" element={<UniversalCoursePage courseType="testing-modern" />} />
          <Route path="/testing-modern-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="testing-modern" />} />
          
          {/* Web3 Development courses */}
          <Route path="/web3-development-courses" element={<UniversalCoursePage courseType="web3-development" />} />
          <Route path="/web3-development-courses/:courseId" element={<UniversalCoursePage courseType="web3-development" />} />
          <Route path="/web3-development-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="web3-development" />} />
          
          {/* Serverless Edge courses */}
          <Route path="/serverless-edge-courses" element={<UniversalCoursePage courseType="serverless-edge" />} />
          <Route path="/serverless-edge-courses/:courseId" element={<UniversalCoursePage courseType="serverless-edge" />} />
          <Route path="/serverless-edge-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="serverless-edge" />} />
          
          {/* RPA Tools courses */}
          <Route path="/rpa-tools-courses" element={<UniversalCoursePage courseType="rpa-tools" />} />
          <Route path="/rpa-tools-courses/:courseId" element={<UniversalCoursePage courseType="rpa-tools" />} />
          <Route path="/rpa-tools-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="rpa-tools" />} />
          
          {/* MLOps & AI Deployment courses */}
          <Route path="/mlops-courses" element={<UniversalCoursePage courseType="mlops" />} />
          <Route path="/mlops-courses/:courseId" element={<UniversalCoursePage courseType="mlops" />} />
          <Route path="/mlops-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="mlops" />} />

          {/* Specialized AI Platforms courses */}
          <Route path="/specialized-ai-courses" element={<UniversalCoursePage courseType="specialized-ai" />} />
          <Route path="/specialized-ai-courses/:courseId" element={<UniversalCoursePage courseType="specialized-ai" />} />
          <Route path="/specialized-ai-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="specialized-ai" />} />
          
          {/* Cloud Computing courses */}
          <Route path="/cloud-courses" element={<UniversalCoursePage courseType="cloud" />} />
          <Route path="/cloud-courses/:courseId" element={<UniversalCoursePage courseType="cloud" />} />
          <Route path="/cloud-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="cloud" />} />
          
          {/* Programming Fundamentals courses */}
          <Route path="/programming-courses" element={<UniversalCoursePage courseType="programming" />} />
          <Route path="/programming-courses/:courseId" element={<UniversalCoursePage courseType="programming" />} />
          <Route path="/programming-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="programming" />} />
          
          {/* Automation & Workflow courses */}
          <Route path="/automation-courses" element={<UniversalCoursePage courseType="automation" />} />
          <Route path="/automation-courses/:courseId" element={<UniversalCoursePage courseType="automation" />} />
          <Route path="/automation-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="automation" />} />

          {/* No-Code Development courses */}
          <Route path="/no-code-courses" element={<UniversalCoursePage courseType="no-code" />} />
          <Route path="/no-code-courses/:courseId" element={<UniversalCoursePage courseType="no-code" />} />
          <Route path="/no-code-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="no-code" />} />

          {/* Rust Programming courses */}
          <Route path="/rust-courses" element={<UniversalCoursePage courseType="rust" />} />
          <Route path="/rust-courses/:courseId" element={<UniversalCoursePage courseType="rust" />} />
          <Route path="/rust-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="rust" />} />

          {/* Edge Computing courses */}
          <Route path="/edge-computing-courses" element={<UniversalCoursePage courseType="edge-computing" />} />
          <Route path="/edge-computing-courses/:courseId" element={<UniversalCoursePage courseType="edge-computing" />} />
          <Route path="/edge-computing-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="edge-computing" />} />

          {/* Green Tech & Sustainability courses */}
          <Route path="/green-tech-courses" element={<UniversalCoursePage courseType="green-tech" />} />
          <Route path="/green-tech-courses/:courseId" element={<UniversalCoursePage courseType="green-tech" />} />
          <Route path="/green-tech-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="green-tech" />} />

          {/* Cross-Platform Development courses */}
          <Route path="/cross-platform-courses" element={<UniversalCoursePage courseType="cross-platform" />} />
          <Route path="/cross-platform-courses/:courseId" element={<UniversalCoursePage courseType="cross-platform" />} />
          <Route path="/cross-platform-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="cross-platform" />} />

          {/* Modern Frontend Frameworks courses */}
          <Route path="/modern-frontend-courses" element={<UniversalCoursePage courseType="modern-frontend" />} />
          <Route path="/modern-frontend-courses/:courseId" element={<UniversalCoursePage courseType="modern-frontend" />} />
          <Route path="/modern-frontend-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="modern-frontend" />} />

          {/* Advanced API Development courses */}
          <Route path="/advanced-api-courses" element={<UniversalCoursePage courseType="advanced-api" />} />
          <Route path="/advanced-api-courses/:courseId" element={<UniversalCoursePage courseType="advanced-api" />} />
          <Route path="/advanced-api-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="advanced-api" />} />

          {/* Kubernetes Mastery courses */}
          <Route path="/kubernetes-mastery-courses" element={<UniversalCoursePage courseType="kubernetes-mastery" />} />
          <Route path="/kubernetes-mastery-courses/:courseId" element={<UniversalCoursePage courseType="kubernetes-mastery" />} />
          <Route path="/kubernetes-mastery-courses/:courseId/:moduleId" element={<UniversalModulePage courseType="kubernetes-mastery" />} />
          
          {/* Catch-all route redirects to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </NavigationAnalyticsProvider>
              </ProtectedLayout>
            </PasswordChangeGuard>
          } />
        </Routes>
      </>
    );
  }

  // Public routes (no layout wrapper) - redirect protected routes to login
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/certificate/verify/:certificateId" element={<CertificateVerify />} />
      {/* Redirect protected routes to login when not authenticated */}
      <Route path="/dashboard" element={<Login />} />
      <Route path="/profile" element={<Login />} />
      <Route path="/settings" element={<Login />} />
      <Route path="/certificates" element={<Login />} />
      <Route path="/admin" element={<Login />} />
      <Route path="/staff" element={<Login />} />
      <Route path="/courses" element={<Login />} />
      <Route path="*" element={<Login />} />
    </Routes>
  );
};

function App() {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const { isInstallable } = usePWA();
  const isFetching = useIsFetching();

  useEffect(() => {
    // Initialize PWA service
    pwaService.initialize();

    // Show install prompt after 30 seconds on mobile if not installed
    if (!pwaService.getInstallationStatus() && isInstallable) {
      const timer = setTimeout(() => {
        setShowInstallPrompt(true);
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, [isInstallable]);

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <FeatureFlagProvider>
        <FocusMusicProvider>
          <AudioProvider>
            <Router>
              <NavigationAwareErrorBoundary>
                <NavigationAudioListener />
                <GlobalLoadingBar isLoading={isFetching > 0} />
                <ScrollToTop />
                <SecurityInitializer />
                <ContentGenerationBootstrap />
                <MasterContentBootstrap />
                <NoBreakageSmokeTests />
                {/* Auto-migration components removed - content enhancement complete */}
                <AppRoutes />
                <FocusMusicWidget />
                <MiniAudioPlayer />
                <OfflineIndicator />
                <UpdatePrompt />
                {showInstallPrompt && (
                  <PWAInstallPrompt 
                    onDismiss={() => setShowInstallPrompt(false)} 
                  />
                )}
              </NavigationAwareErrorBoundary>
            </Router>
          </AudioProvider>
        </FocusMusicProvider>
      </FeatureFlagProvider>
    </ThemeProvider>
  );
}

export default App;
